import { Check, PeopleAlt } from "@mui/icons-material";
import classNames from "classnames";
import { MouseEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import UserService from "../../api/clockodo/UserService";
import Modal from "../../components/Modal/Modal";
import { NoConnectionModal } from "../../components/NoConnectionModal/NoConnectionModal";
import Clock from "../../components/Timer/Clock";
import { useHandleService } from "../../hooks/useHandleService";
import { useScreenSize } from "../../hooks/useScreenSize";
import { WarningIcon } from "../../icons/WarningIcon/WarningIcon";
import { RootState } from "../../store";
import { userSliceActions } from "../../store/user-slice";
import { workersActions } from "../../store/workers-slice";

import styles from "./header.module.css";

type HeaderProps = {
  withBackButton?: boolean;
  isMinimized?: boolean;
  onClick: Function;
  onGoBack?: MouseEventHandler<HTMLImageElement>;
};

export const Header = ({
  withBackButton,
  isMinimized,
  onClick,
  onGoBack,
}: HeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { isDesktop } = useScreenSize();
  const { handlePauseService, handleResumeService } = useHandleService();

  const [isPauseModalOpen, setPauseModalOpen] = useState<boolean>(false);
  const [isResumeModalOpen, setResumeModalOpen] = useState<boolean>(false);
  const [showNoConnectionModal, setShowNoConnectionModal] = useState(false);

  const isCheckedIn = useSelector((state: RootState) => state.user.isCheckedIn);
  const isOnline = useSelector((state: RootState) => state.online.isOnline);
  const profileName = useSelector((state: RootState) => state.user.profileName);
  const clockodoUser = useSelector(
    (state: RootState) => state.user.clockodoUser
  );
  const activeWorkers = useSelector(
    (state: RootState) => state.workers.totalActive
  );

  const clocks = useSelector((state: RootState) => state.clock.clocks);

  const clockList = Object.values(clocks ?? {});

  const runningClock = clockList.find((clock) => clock.isRunning);

  const newestPausedClock = clockList
    .filter((clock) => clock.isPaused && clock.lastEventTimestamp)
    .sort(
      (a, b) =>
        new Date(b.lastEventTimestamp!).getTime() -
        new Date(a.lastEventTimestamp!).getTime()
    )[0];

  const currentClock = runningClock ?? newestPausedClock;

  const [activeWorkersMessage, setActiveWorkersMessage] = useState(
    "No coworkers online"
  );

  // Check if user is checked in (Clockodo)
  useEffect(() => {
    UserService.GetActiveUsers()
      .then(({ data: { entries } }) => {
        // Check if logged in user is checked in
        const checkedInUserEntry = entries.find(
          (entry) => entry.usersId === clockodoUser?.id
        );

        dispatch(userSliceActions.setIsCheckedIn(!!checkedInUserEntry));

        if (checkedInUserEntry) {
          dispatch(userSliceActions.setClockId(checkedInUserEntry.id));
        }

        // Update active users in redux state
        dispatch(
          workersActions.setActive(
            entries.map((entry) => ({
              id: entry.usersId,
              usersName: entry.usersName,
              initials: entry.usersName.substring(0, 2).toUpperCase(),
              text: "At Work",
              status: "working",
            }))
          )
        );
        dispatch(workersActions.setTotalActive(entries.length));
      })
      .catch();
  }, [clockodoUser?.id]);

  useEffect(() => {
    if (activeWorkers > 0) {
      setActiveWorkersMessage(
        `${activeWorkers} coworker${activeWorkers > 1 ? "s" : ""} online`
      );
    }
  }, [activeWorkers]);

  const onClickClock = () => {
    if (!currentClock) {
      return;
    }

    if (currentClock.isRunning) {
      setPauseModalOpen(true);
      return;
    }

    if (currentClock.isPaused) {
      setResumeModalOpen(true);
      return;
    }
  };

  const handleCloseModal = () => {
    setShowNoConnectionModal(false);
  };

  return (
    <div>
      <Modal
        isOpen={isPauseModalOpen}
        message={t("PauseService")}
        note="PauseMessage"
        iconType="pause"
        onConfirm={async () => {
          await handlePauseService();
          setPauseModalOpen(false);
        }}
        onCancel={() => setPauseModalOpen(false)}
        cancelText="Cancel"
        confirmText="Pause"
        confirmButtonColor="#F29100"
        closeButtonColor="#EDEFF5"
        cancelTextColor="#3D5170"
      />

      <Modal
        isOpen={isResumeModalOpen}
        message={t("ResumeService")}
        note="ResumeMessage"
        iconType="play"
        onConfirm={async () => {
          await handleResumeService();
          setResumeModalOpen(false);
        }}
        onCancel={() => setResumeModalOpen(false)}
        cancelText="Cancel"
        confirmText="Resume"
        confirmButtonColor="#11A8B8"
        closeButtonColor="#EDEFF5"
        cancelTextColor="#3D5170"
      />

      <div
        onClick={() => onClick()}
        className={classNames(
          styles["header"],
          {
            [styles["header--green"]]:
              isCheckedIn || currentClock?.isRunning || currentClock?.isPaused,
          },
          {
            [styles["desktop-header"]]: isDesktop,
          },
          {
            [styles["header--minimized"]]: isMinimized,
          }
        )}
      >
        {!isMinimized && isOnline && (
          <div className={styles["header__welcome-message"]}>
            <p>
              {t("Welcome") ?? ""}, {profileName}
            </p>
            !{isCheckedIn && " You are checked-in!"}
          </div>
        )}

        <div className={styles["header__actions"]}>
          {withBackButton && (
            <img alt="" src="/img/return-arrow.png" onClick={onGoBack} />
          )}

          {!withBackButton && (
            <button
              className={`${styles["status"]} ${
                !isOnline && styles["status--offline"]
              } ${
                !isOnline && isMinimized && styles["status--offline--minimized"]
              }`}
              onClick={() => {
                if (isOnline) {
                  navigate("/coworkers");
                } else {
                  setShowNoConnectionModal(true);
                }
              }}
            >
              {/* Button text when online */}
              {isOnline && !isMinimized && activeWorkersMessage}
              {isOnline && <PeopleAlt className={styles["status__icon"]} />}

              {/* Button text when offline */}
              {!isOnline && (
                <>
                  <WarningIcon /> <span>{t("NoInternet")}</span>
                </>
              )}
            </button>
          )}

          {isMinimized && currentClock && (
            <div>
              <Clock
                appointmentId={currentClock.appointmentId}
                onClick={onClickClock}
              />
            </div>
          )}

          {!isCheckedIn && (
            <button
              className={styles["checkin"]}
              onClick={() => navigate("/check-in")}
            >
              <>Check-in {!isMinimized && <>👋</>}</>
            </button>
          )}

          {isCheckedIn && (
            <button
              className={styles["checkin"]}
              onClick={() => navigate("/check-in")}
            >
              <>
                <Check />
              </>
            </button>
          )}
        </div>
        {showNoConnectionModal && (
          <NoConnectionModal onClose={handleCloseModal} />
        )}
      </div>
    </div>
  );
};
